import moment from 'moment'
import * as React from 'react'
import { Trash2 } from 'react-feather'
import { useTranslation } from 'react-i18next'

import { LOCIZE_NAMESPACE } from 'main/common'
import { i18nKeyPrefix } from 'main/components/tour/i18n'
import { IOpening } from 'main/components/tour/RequestATourModal/Content'
import { ContentContainer } from 'main/components/tour/RequestATourModal/Content.styles'
import { RequestTourModal_school } from 'main/components/tour/RequestATourModal/gql/__generated__/RequestTourModal'
import SchoolSummary from 'main/components/tour/ScheduleTourModal/common/SchoolSummary'
import { Row, TimesContainer } from './Availability.styles'

import { Button, DatePicker, palette, Small, Strong, Text, TimeSelect } from 'main/ui'

const PRE = `${i18nKeyPrefix}-REQUEST-MODAL-AVAILABILITY`

interface IProps {
  school: RequestTourModal_school
  proposedOpenings: IOpening[]
  setProposedOpenings: (openings: IOpening[]) => void
  onComplete: () => void
}

export default function Availability(props: IProps) {
  const { school, proposedOpenings, setProposedOpenings, onComplete } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const hasProposedOpenings =
    proposedOpenings.length > 0 &&
    proposedOpenings.every(opening => opening.beginsAt && opening.date)

  const onDateSelection = (selectedDate: Date) => {
    let beginsAt = '09:00:00'
    const today = new Date()

    if (selectedDate.toDateString() === today.toDateString()) {
      const currentMoment = moment()
      const minutes = currentMoment.minutes()
      const roundedMinutes = Math.ceil(minutes / 15) * 15
      const roundedTime = currentMoment.minutes(roundedMinutes).format('HH:mm:00')

      beginsAt = roundedTime
      if (roundedTime < '08:45:00') beginsAt = '09:00:00'
      if (roundedTime > '21:15:00') beginsAt = '21:00:00'
    }

    const availability = { date: selectedDate, beginsAt }
    setProposedOpenings([...proposedOpenings, availability])
  }

  const updateTimeSelection = (beginsAt: string | null, idx: number) => {
    if (!beginsAt) return

    const updatedDates = proposedOpenings.map((opening, id) => {
      if (id !== idx) return opening
      return { ...opening, beginsAt }
    })

    setProposedOpenings(updatedDates)
  }

  const deleteAvailability = (idx: number) => {
    const updatedAvailabilities = proposedOpenings.filter((_opening, index) => index !== idx)
    setProposedOpenings([...updatedAvailabilities])
  }

  // 1 day from now
  const minDate = new Date()
  minDate.setDate(minDate.getDate() + 1)

  return (
    <ContentContainer>
      <SchoolSummary school={school} />
      <Text>{t(`${PRE}-SUMMARY`)}</Text>
      <Small>{t(`${PRE}-SUMMARY-2`)}</Small>
      <DatePicker
        dates={proposedOpenings.map(date => date.date)}
        onDateSelection={onDateSelection}
        minDate={minDate}
        disabled={proposedOpenings.length >= 3}
      />
      <Strong>{t(`${PRE}-SUMMARY-3`)}</Strong>
      <Small>{t(`${PRE}-SUMMARY-4`)}</Small>
      <TimesContainer>
        {proposedOpenings.map((currentOpening, idx) => {
          const isInvalid =
            proposedOpenings.filter(
              opening =>
                opening.date.getTime() === currentOpening.date.getTime() &&
                opening.beginsAt === currentOpening.beginsAt,
            ).length > 1

          const today = new Date()
          const minTime =
            currentOpening.date.toDateString() === today.toDateString()
              ? `${today.getHours()}:${today.getMinutes()}:00.`
              : '08:45:00'

          return (
            <Row key={idx}>
              <Text>{moment(currentOpening.date).format('dddd, MMMM D')}</Text>
              <TimeSelect
                label={t(`${PRE}-TIME-LBL`)}
                value={currentOpening.beginsAt}
                roundedCorners
                invalid={isInvalid}
                onChange={time => updateTimeSelection(time, idx)}
                min={minTime}
                max="21:15:00"
              />
              <Trash2 size={24} color={palette.slate} onClick={() => deleteAvailability(idx)} />
            </Row>
          )
        })}
      </TimesContainer>

      <Button full onClick={() => onComplete()} disabled={!hasProposedOpenings}>
        {t(`${PRE}-BTN`)}
      </Button>
    </ContentContainer>
  )
}
